import PropTypes from 'prop-types';
import {
  IconButton,
  Avatar,
  Badge,
  Box,
  Stack,
  SvgIcon,
  Tabs,
  Tab,
  Tooltip,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import NotificationsIcon from '@mui/icons-material/NotificationsNone';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import { useHistory, useLocation } from 'react-router-dom';
import usePopover from 'src/hooks/usePopover';
import AccountPopover from 'src/common/AccountPopover';
import { useSelector } from 'react-redux';
import useAuth from 'src/context/AuthContext';
import ROUTE from 'src/utils/routeConstants';
import { FLEET_ONLY_BRAND, isFleetOnlyModeEnabled } from 'src/utils/appMode';
import { USER_ROLES } from 'src/utils/constants';

export const SIDE_NAV_WIDTH = 280;
export const COLLAPSED_SIDE_NAV_WIDTH = 80;
const TOP_NAV_HEIGHT = 64;
const MOBILE_TOP_NAV_HEIGHT = 64;

// Helper to get initials safely
const getInitials = (name = '') => name
  .replace(/\s+/, ' ')
  .split(' ')
  .slice(0, 2)
  .map((v) => v && v[0].toUpperCase())
  .join('');

const DashboardNavbar = ({
  onNavOpen,
  onNavClose,
  onToggleCollapse,
  isCollapsed,
  sideNavWidth,
  mobileNavOpen = false
}) => {
  const theme = useTheme();
  const lgUp = useMediaQuery(theme.breakpoints.up('lg'));
  const accountPopover = usePopover();
  const { unreadCount } = useSelector((state) => state.notifications);
  const { appSettings } = useSelector((state) => state.settings);
  const isFleetOnlyMode = isFleetOnlyModeEnabled(appSettings);
  const history = useHistory();
  const location = useLocation();
  const { getUser } = useAuth();
  const user = getUser();
  const effectiveWidth = lgUp ? (sideNavWidth ?? SIDE_NAV_WIDTH) : 0;
  const isOperator = user?.role === USER_ROLES.operator;
  const showAnalyticsTabs = !!appSettings?.fleet_management
    && location.pathname === ROUTE.dashboard
    && !isFleetOnlyMode;
  const analyticsTabValue = new URLSearchParams(location.search).get('analytics') === 'fleet'
    ? 'fleet'
    : 'fuel';

  const userDisplayName = user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : '';
  const userInitials = getInitials(userDisplayName);

  const handleAnalyticsTabChange = (event, newValue) => {
    if (!newValue) {
      return;
    }

    const searchParams = new URLSearchParams(location.search);
    if (newValue === 'fleet') {
      searchParams.set('analytics', 'fleet');
    } else {
      searchParams.delete('analytics');
    }

    const search = searchParams.toString();
    history.push({
      pathname: ROUTE.dashboard,
      search: search ? `?${search}` : ''
    });
  };

  return (
    <>
      <Box
        component="header"
        sx={{
          backdropFilter: 'blur(20px)',
          background: isFleetOnlyMode
            ? `linear-gradient(90deg, ${alpha(FLEET_ONLY_BRAND.canvas, 0.96)}, ${alpha('#EAF2F8', 0.92)})`
            : `linear-gradient(90deg, ${alpha(theme.palette.background.paper, 0.9)}, ${alpha(theme.palette.background.default, 0.8)})`,
          position: 'sticky',
          left: { lg: `${effectiveWidth}px` },
          top: 0,
          width: { lg: `calc(100% - ${effectiveWidth}px)` },
          zIndex: theme.zIndex.appBar + 1,
          borderBottom: isFleetOnlyMode
            ? `1px solid ${FLEET_ONLY_BRAND.border}`
            : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          transition: 'width 300ms cubic-bezier(0.4, 0, 0.2, 1), left 300ms cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
        <Stack
          alignItems="center"
          direction="row"
          justifyContent="space-between"
          sx={{
            minHeight: {
              xs: showAnalyticsTabs ? 112 : MOBILE_TOP_NAV_HEIGHT,
              sm: TOP_NAV_HEIGHT
            },
            flexWrap: {
              xs: showAnalyticsTabs ? 'wrap' : 'nowrap',
              md: 'nowrap'
            },
            rowGap: showAnalyticsTabs ? 1.5 : 0,
            px: {
              xs: 2,
              sm: 3
            },
            py: showAnalyticsTabs ? 1 : 0,
            position: 'relative'
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{ order: 1 }}
          >
            {!lgUp && (
              <IconButton
                onClick={onNavOpen}
                sx={{ color: isFleetOnlyMode ? FLEET_ONLY_BRAND.ink : 'text.secondary' }}
              >
                <MenuOpenIcon
                  sx={{
                    transform: mobileNavOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s'
                  }}
                />
              </IconButton>
            )}
            {lgUp && (
              <IconButton
                onClick={onToggleCollapse}
                sx={{ color: isFleetOnlyMode ? FLEET_ONLY_BRAND.ink : 'text.secondary' }}
              >
                <MenuOpenIcon
                  sx={{
                    transform: isCollapsed ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s'
                  }}
                />
              </IconButton>
            )}
          </Stack>

          {showAnalyticsTabs && (
            <Box
              sx={{
                order: { xs: 3, md: 2 },
                width: { xs: '100%', md: 'auto' },
                display: 'flex',
                justifyContent: 'center',
                position: { md: 'absolute' },
                left: { md: '50%' },
                transform: { md: 'translateX(-50%)' }
              }}
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  bgcolor: alpha(theme.palette.background.paper, 0.92),
                  p: 0.5,
                  borderRadius: 3,
                  boxShadow: '0 2px 10px 0 rgba(0,0,0,0.04)',
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Tabs
                  value={analyticsTabValue}
                  onChange={handleAnalyticsTabChange}
                  TabIndicatorProps={{ style: { display: 'none' } }}
                  sx={{
                    minHeight: 38,
                    '& .MuiTab-root': {
                      minHeight: 38,
                      minWidth: { xs: 118, sm: 138 },
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: { xs: '0.85rem', sm: '0.95rem' },
                      borderRadius: 2,
                      px: { xs: 2, sm: 3 },
                      py: 1,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      color: 'text.secondary',
                      '&:hover': {
                        color: 'text.primary',
                        bgcolor: alpha(theme.palette.action.hover, 0.5)
                      },
                      '&.Mui-selected': {
                        color: 'primary.main',
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        boxShadow: '0 2px 8px 0 rgba(0,0,0,0.04)'
                      }
                    }
                  }}
                  aria-label="Dashboard Analytics Tabs"
                >
                  <Tab value="fuel" label="Fuel Analytics" disableRipple />
                  <Tab value="fleet" label="Fleet Analytics" disableRipple />
                </Tabs>
              </Box>
            </Box>
          )}

          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{ order: 2, ml: 'auto' }}
          >
            {!lgUp && mobileNavOpen && (
              <IconButton
                onClick={onNavClose}
                sx={{ color: isFleetOnlyMode ? FLEET_ONLY_BRAND.primary : 'error.main' }}
              >
                <CloseIcon />
              </IconButton>
            )}
            {!isOperator && (
              <Tooltip title="Notifications">
                <IconButton
                  sx={{ color: isFleetOnlyMode ? FLEET_ONLY_BRAND.ink : 'text.secondary' }}
                  onClick={() => history.push(ROUTE.notifications)}
                >
                  <Badge
                    badgeContent={unreadCount > 0 ? unreadCount : null}
                    color="error"
                  >
                    <SvgIcon fontSize="medium">
                      <NotificationsIcon />
                    </SvgIcon>
                  </Badge>
                </IconButton>
              </Tooltip>
            )}

            {/* Enhanced User Profile Icon */}
            <Tooltip title="Account Settings">
              <Box
                onClick={accountPopover?.handleOpen}
                ref={accountPopover?.anchorRef}
                sx={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  position: 'relative'
                }}
              >
                {/* Online Status Badge */}
                <Badge
                  overlap="circular"
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right'
                  }}
                  variant="dot"
                  sx={{
                    '& .MuiBadge-badge': {
                      backgroundColor: '#44b700',
                      color: '#44b700',
                      boxShadow: `0 0 0 2px ${isFleetOnlyMode ? FLEET_ONLY_BRAND.canvas : theme.palette.background.paper}`,
                      '&::after': {
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        animation: 'ripple 1.2s infinite ease-in-out',
                        border: '1px solid currentColor',
                        content: '""'
                      }
                    },
                    '@keyframes ripple': {
                      '0%': {
                        transform: 'scale(.8)',
                        opacity: 1
                      },
                      '100%': {
                        transform: 'scale(2.4)',
                        opacity: 0
                      }
                    }
                  }}
                >
                  <Avatar
                    sx={{
                      height: 40,
                      width: 40,
                      // Gradient Background
                      background: isFleetOnlyMode
                        ? `linear-gradient(135deg, ${FLEET_ONLY_BRAND.primary} 0%, ${FLEET_ONLY_BRAND.primarySoft} 100%)`
                        : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      fontWeight: 700,
                      fontSize: '0.95rem',
                      color: '#ffffff',
                      border: '2px solid transparent',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        boxShadow: isFleetOnlyMode
                          ? `0 0 0 4px ${alpha(FLEET_ONLY_BRAND.primarySoft, 0.18)}`
                          : `0 0 0 4px ${alpha(theme.palette.primary.main, 0.2)}`,
                        transform: 'scale(1.05)'
                      }
                    }}
                  >
                    {userInitials || <PersonIcon />}
                  </Avatar>
                </Badge>
              </Box>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>
      <AccountPopover
        anchorEl={accountPopover.anchorRef.current}
        open={accountPopover.open}
        onClose={accountPopover.handleClose}
      />
    </>
  );
};

DashboardNavbar.propTypes = {
  onNavOpen: PropTypes.func,
  onNavClose: PropTypes.func,
  onToggleCollapse: PropTypes.func,
  isCollapsed: PropTypes.bool,
  sideNavWidth: PropTypes.number,
  mobileNavOpen: PropTypes.bool
};

export default DashboardNavbar;
